import React, { useState, useEffect, useRef, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { ProgressSpinner } from 'primereact/progressspinner';
import { Message } from 'primereact/message';
import { useStudentProfileStore } from '../stateManagers/stores/studentProfileStore';
import apiClient from '../stateManagers/apiSetup/axiosInterceptor';

/* ─── Design Tokens ────────────────────────────────────────────────── */
const C = {
  navy:        '#0e225d',
  navyMid:     '#1a3370',
  navyDark:    '#0a1a3d',
  navyHover:   'rgba(14,34,93,0.06)',
  gold:        '#fbb21f',
  goldDim:     'rgba(251,178,31,0.14)',
  goldBorder:  'rgba(251,178,31,0.35)',
  white:       '#ffffff',
  border:      '#e5e7eb',
  muted:       '#9ca3af',
  sub:         '#6b7280',
  body:        '#1f2937',
  green:       '#16a34a',
  greenDim:    'rgba(22,163,74,0.12)',
  greenBorder: 'rgba(22,163,74,0.3)',
  red:         '#dc2626',
  redDim:      'rgba(220,38,38,0.1)',
  redBorder:   'rgba(220,38,38,0.25)',
  blue:        '#2563eb',
  blueDim:     'rgba(37,99,235,0.1)',
  blueBorder:  'rgba(37,99,235,0.25)',
};

/* ─── Types ─────────────────────────────────────────────────────────── */
interface Announcement {
  announcement_id:    string;
  batch_id:           string | null;
  batch_name:         string | null;
  announcement_title: string;
  announcement_body:  string;
  created_at:         string;
}

interface Notice {
  notice_id:      string;
  notice_title:   string;
  notice_body:    string;
  created_at:     string;
  broadcast_type: string;
}

interface AnnouncementsResponse {
  success: boolean;
  data:    Announcement[];
}

interface NoticesResponse {
  success: boolean;
  data:    Notice[];
}

interface Assignment {
  assignment_id:    string;
  batch_id:         string | null;
  batch_name:       string | null;
  assignment_title: string;
  notes:            string;
  submission_date:  string | null;
  submission_time:  string | null;
  created_at:       string;
}

interface AssignmentsResponse {
  success: boolean;
  data:    Assignment[];
}

type FeePaymentStatus = 'fully_paid' | 'partial' | 'not_paid';

interface FeesInfo {
  base_fee:           number;
  additional_fee:     number;
  total_fee:          number;
  total_paid:         number;
  balance_due:        number;
  payment_status:     FeePaymentStatus;
  payment_count:      number;
  last_payment_date:  string | null;
  fees_updated_at:    string | null;
}

interface FeeDetail {
  enrollment_id:      string;
  course_id:          string | null;
  course_name:        string | null;
  course_title:       string | null;
  batch_id:           string | null;
  batch_name:         string | null;
  batch_time:         string | null;
  start_month:        string | null;
  start_year:         number | null;
  end_month:          string | null;
  end_year:           number | null;
  date_of_admission:  string | null;
  fees:               FeesInfo;
}

interface StudentFeesResponse {
  success: boolean;
  data: {
    student_id:        string;
    student_name:      string | null;
    email:              string | null;
    mobile:             string | null;
    institute_id:       string | null;
    branch_id:          string | null;
    enrollments_count:  number;
    fees_details:       FeeDetail[];
    total_fees_summary: {
      total_base_fee:       number;
      total_additional_fee: number;
      grand_total:          number;
    };
  };
}

const FEE_STATUS_META: Record<FeePaymentStatus, { label: string; color: string }> = {
  fully_paid: { label: 'Paid',    color: C.green },
  partial:    { label: 'Partial', color: C.gold  },
  not_paid:   { label: 'Unpaid',  color: C.red   },
};

type AttendanceStatus = 'present' | 'absent' | 'late' | 'excused';

interface AttendanceRecordItem {
  batch_id:        string;
  course_id:       string | null;
  attendance_date: string;
  topic:           string | null;
  status:          AttendanceStatus;
}

interface AttendanceByStudentResponse {
  success: boolean;
  data: {
    student_id:   string;
    student_name: string;
    summary: {
      total_days: number;
      present:    number;
      absent:     number;
      late:       number;
      excused:    number;
      attendance_percentage: number;
    };
    records: AttendanceRecordItem[];
  };
}

interface BatchAttendanceStat {
  batch_id:    string;
  batch_name:  string;
  total:       number;
  present:     number;
  absent:      number;
  late:        number;
  excused:     number;
  percentage:  number;
}

const STATUS_META: Record<AttendanceStatus, { label: string; color: string }> = {
  present: { label: 'Present', color: C.green },
  absent:  { label: 'Absent',  color: C.red   },
  late:    { label: 'Late',    color: C.gold  },
  excused: { label: 'Excused', color: C.blue  },
};

const LIMIT = 5;

const MONTH_NAMES = ['January', 'February', 'March', 'April', 'May', 'June', 'July', 'August', 'September', 'October', 'November', 'December'];
const MONTH_SHORT = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
const WEEKDAY_SHORT = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];

const formatMonthYear = (month: string | null, year: number | null) => {
  if (!month && !year) return null;
  const monthLabel = month ? (MONTH_SHORT[Number(month) - 1] ?? month) : '';
  return [monthLabel, year].filter(Boolean).join(' ');
};

const formatDate = (dateString: string | null) => {
  if (!dateString) return '—';
  const date = new Date(dateString);
  if (Number.isNaN(date.getTime())) return dateString;
  return date.toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric' });
};

const formatDateKeyLong = (dateKey: string) => {
  const date = new Date(`${dateKey}T00:00:00`);
  if (Number.isNaN(date.getTime())) return dateKey;
  return date.toLocaleDateString('en-IN', { weekday: 'short', day: '2-digit', month: 'short', year: 'numeric' });
};

const toDateKey = (dateString: string) => {
  // normalizes "2026-09-03" or "2026-09-03T00:00:00.000Z" etc. to "2026-09-03"
  const d = new Date(dateString);
  if (Number.isNaN(d.getTime())) return dateString.slice(0, 10);
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, '0');
  const day = String(d.getDate()).padStart(2, '0');
  return `${y}-${m}-${day}`;
};

const getGreeting = () => {
  const hour = new Date().getHours();
  if (hour < 12) return 'Good Morning';
  if (hour < 17) return 'Good Afternoon';
  return 'Good Evening';
};

const formatCurrency = (amount: number) => `₹${Math.round(amount).toLocaleString('en-IN')}`;

const truncate = (text: string, max: number) => (text.length > max ? `${text.slice(0, max - 1)}…` : text);

/* ─── Donut chart (pure SVG, no chart library) ───────────────────────── */
const DonutChart: React.FC<{ stats: BatchAttendanceStat | null; size?: number }> = ({ stats, size = 130 }) => {
  const strokeWidth = 14;
  const radius = (size - strokeWidth) / 2;
  const circumference = 2 * Math.PI * radius;
  const center = size / 2;

  const segments: { key: AttendanceStatus; value: number }[] = stats
    ? [
        { key: 'present' as AttendanceStatus, value: stats.present },
        { key: 'late' as AttendanceStatus, value: stats.late },
        { key: 'excused' as AttendanceStatus, value: stats.excused },
        { key: 'absent' as AttendanceStatus, value: stats.absent },
      ].filter(s => s.value > 0)
    : [];

  const total = stats?.total ?? 0;
  let cumulative = 0;

  return (
    <svg width={size} height={size} viewBox={`0 0 ${size} ${size}`} style={{ transform: 'rotate(-90deg)' }}>
      {total === 0 ? (
        <circle cx={center} cy={center} r={radius} fill="none" stroke="#f1f2f4" strokeWidth={strokeWidth} />
      ) : (
        segments.map(seg => {
          const fraction = seg.value / total;
          const dash = fraction * circumference;
          const gap = circumference - dash;
          const offset = -cumulative * circumference;
          cumulative += fraction;
          return (
            <circle
              key={seg.key}
              cx={center}
              cy={center}
              r={radius}
              fill="none"
              stroke={STATUS_META[seg.key].color}
              strokeWidth={strokeWidth}
              strokeDasharray={`${dash} ${gap}`}
              strokeDashoffset={offset}
              strokeLinecap="butt"
            />
          );
        })
      )}
    </svg>
  );
};

/* ─── Fees bar chart (pure divs, no chart library) ───────────────────
   One horizontal stacked bar per enrollment: paid (green) + balance (red),
   scaled against the largest total_fee across all enrollments so bars are
   comparable to each other. */
const FeesBarChart: React.FC<{ data: FeeDetail[] }> = ({ data }) => {
  const maxTotal = Math.max(1, ...data.map(d => d.fees?.total_fee ?? 0));

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: '14px' }}>
      {data.map(fd => {
        const total = fd.fees?.total_fee ?? 0;
        const paid = fd.fees?.total_paid ?? 0;
        const balance = fd.fees?.balance_due ?? 0;
        const trackWidthPct = total > 0 ? Math.max(6, (total / maxTotal) * 100) : 4;
        const paidPct = total > 0 ? Math.min(100, (paid / total) * 100) : 0;
        const balancePct = total > 0 ? Math.max(0, 100 - paidPct) : 100;
        const label = fd.course_name ?? fd.course_title ?? 'Course';

        return (
          <div key={fd.enrollment_id}>
            <div style={{ display: 'flex', alignItems: 'baseline', justifyContent: 'space-between', marginBottom: '5px', gap: '10px' }}>
              <span style={{ fontSize: '0.76rem', fontWeight: '700', color: C.navy, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                {truncate(label, 30)}{fd.batch_name ? ` · ${truncate(fd.batch_name, 16)}` : ''}
              </span>
              <span style={{ fontSize: '0.7rem', color: C.muted, fontWeight: '600', flexShrink: 0 }}>
                {formatCurrency(total)}
              </span>
            </div>
            <div style={{ width: '100%', height: '16px', borderRadius: '6px', background: '#f1f2f4', overflow: 'hidden' }}>
              <div style={{ width: `${trackWidthPct}%`, height: '100%', display: 'flex' }}>
                {paidPct > 0 && (
                  <div
                    title={`Paid: ${formatCurrency(paid)}`}
                    style={{
                      width: `${paidPct}%`, height: '100%', background: `linear-gradient(90deg, ${C.green}, #22c55e)`,
                      borderRadius: balance > 0 ? '6px 0 0 6px' : '6px',
                    }}
                  />
                )}
                {balance > 0 && (
                  <div
                    title={`Balance due: ${formatCurrency(balance)}`}
                    style={{
                      width: `${balancePct}%`, height: '100%', background: `linear-gradient(90deg, #f87171, ${C.red})`,
                      borderRadius: paidPct > 0 ? '0 6px 6px 0' : '6px',
                    }}
                  />
                )}
              </div>
            </div>
            <div style={{ display: 'flex', justifyContent: 'space-between', marginTop: '4px' }}>
              <span style={{ fontSize: '0.66rem', color: C.green, fontWeight: '700' }}>Paid {formatCurrency(paid)}</span>
              {balance > 0 && <span style={{ fontSize: '0.66rem', color: C.red, fontWeight: '700' }}>Due {formatCurrency(balance)}</span>}
            </div>
          </div>
        );
      })}

      <div style={{ display: 'flex', gap: '14px', paddingTop: '2px' }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: '5px' }}>
          <span style={{ width: '9px', height: '9px', borderRadius: '2px', background: C.green, display: 'inline-block' }} />
          <span style={{ fontSize: '0.68rem', color: C.sub, fontWeight: '600' }}>Paid</span>
        </div>
        <div style={{ display: 'flex', alignItems: 'center', gap: '5px' }}>
          <span style={{ width: '9px', height: '9px', borderRadius: '2px', background: C.red, display: 'inline-block' }} />
          <span style={{ fontSize: '0.68rem', color: C.sub, fontWeight: '600' }}>Balance due</span>
        </div>
      </div>
    </div>
  );
};

/* ─── Component ─────────────────────────────────────────────────────── */
const Dashboard: React.FC = () => {
  const [announcements, setAnnouncements] = useState<Announcement[]>([]);
  const [notices,       setNotices]       = useState<Notice[]>([]);
  const [assignments,   setAssignments]   = useState<Assignment[]>([]);
  const [feesDetails,   setFeesDetails]   = useState<FeeDetail[]>([]);

  const [batchAttendance, setBatchAttendance] = useState<BatchAttendanceStat[]>([]);
  const [attendanceRecordsByBatch, setAttendanceRecordsByBatch] = useState<Record<string, AttendanceRecordItem[]>>({});
  const [selectedBatchId, setSelectedBatchId] = useState<string | null>(null);
  const [calendarDate, setCalendarDate] = useState<Date>(new Date());
  const [feesExpanded, setFeesExpanded] = useState<Record<string, boolean>>({});

  const [loading,       setLoading]       = useState(true);
  const [assignmentsLoading, setAssignmentsLoading] = useState(true);
  const [attendanceLoading,  setAttendanceLoading]  = useState(true);
  const [feesLoading,   setFeesLoading]   = useState(true);
  const [error,         setError]         = useState<string | null>(null);

  const { profile, isLoading: profileLoading } = useStudentProfileStore();
  const navigate = useNavigate();
  const carouselRef = useRef<HTMLDivElement>(null);

  const branchId = profile?.branch?.branch_id ?? null;
  const studentId = profile?.student_id ?? null;
  const firstName = profile?.name ?? '';
  const profilePicUrl = profile?.profilePicture?.cdn_url ?? null;
  const enrollments = profile?.enrollments ?? [];
  const initials = firstName
    ? firstName.trim().split(/\s+/).slice(0, 2).map(w => w[0]).join('').toUpperCase()
    : 'S';

  const scrollCarousel = (direction: 'left' | 'right') => {
    const el = carouselRef.current;
    if (!el) return;
    const amount = 300;
    el.scrollBy({ left: direction === 'left' ? -amount : amount, behavior: 'smooth' });
  };

  const toggleFeeExpanded = (enrollmentId: string) => {
    setFeesExpanded(prev => ({ ...prev, [enrollmentId]: !prev[enrollmentId] }));
  };

  useEffect(() => {
    if (branchId) fetchDashboardFeeds();
  }, [branchId]);

  useEffect(() => {
    if (branchId) fetchAssignments();
  }, [branchId]);

  useEffect(() => {
    if (studentId) fetchAttendance();
  }, [studentId]);

  useEffect(() => {
    if (studentId) fetchFees();
  }, [studentId]);

  const fetchDashboardFeeds = async () => {
    if (!branchId) return;
    try {
      setLoading(true);
      setError(null);

      const params = new URLSearchParams({ page: '1', limit: String(LIMIT) });

      const [announcementsRes, noticesRes] = await Promise.all([
        apiClient.get<AnnouncementsResponse>(`/v1/announcements/by-branch/${branchId}?${params}`),
        apiClient.get<NoticesResponse>(`/v1/noticeboard/by-branch/${branchId}?${params}`),
      ]);

      if (announcementsRes.data.success) setAnnouncements(announcementsRes.data.data);
      if (noticesRes.data.success) setNotices(noticesRes.data.data);
    } catch (err: any) {
      setError(err.response?.data?.error || 'Failed to load dashboard feeds.');
    } finally {
      setLoading(false);
    }
  };

  const fetchAssignments = async () => {
    try {
      setAssignmentsLoading(true);
      const params = new URLSearchParams({ page: '1', limit: String(LIMIT) });
      const res = await apiClient.get<AssignmentsResponse>(`/v1/assignments/my-enrollments?${params}`);
      if (res.data.success) setAssignments(res.data.data);
    } catch (err) {
      // fail silently on the dashboard — full list is available at /assignments
    } finally {
      setAssignmentsLoading(false);
    }
  };

  const fetchAttendance = async () => {
    if (!studentId) return;
    try {
      setAttendanceLoading(true);
      const res = await apiClient.get<AttendanceByStudentResponse>(`/v1/attendance/student/${studentId}`);
      if (res.data.success) {
        const statsMap: Record<string, BatchAttendanceStat> = {};
        const recordsMap: Record<string, AttendanceRecordItem[]> = {};

        for (const rec of res.data.data.records) {
          const enr = enrollments.find(e => e.batch_id === rec.batch_id);
          if (!statsMap[rec.batch_id]) {
            statsMap[rec.batch_id] = {
              batch_id: rec.batch_id,
              batch_name: enr?.batch_name ?? 'Batch',
              total: 0, present: 0, absent: 0, late: 0, excused: 0, percentage: 0,
            };
          }
          statsMap[rec.batch_id].total += 1;
          statsMap[rec.batch_id][rec.status] += 1;

          if (!recordsMap[rec.batch_id]) recordsMap[rec.batch_id] = [];
          recordsMap[rec.batch_id].push(rec);
        }

        // A student can have multiple enrollments (batches). Make sure every
        // enrolled batch shows up as a tab — including ones with zero
        // attendance marked so far — not just batches that already have records.
        const enrolledBatchIds = enrollments
          .map(e => e.batch_id)
          .filter((id): id is string => !!id);
        const allBatchIds = Array.from(new Set([...enrolledBatchIds, ...Object.keys(statsMap)]));

        const stats = allBatchIds.map(bId => {
          const enr = enrollments.find(e => e.batch_id === bId);
          const existing = statsMap[bId];
          if (existing) {
            return {
              ...existing,
              batch_name: enr?.batch_name ?? existing.batch_name,
              percentage: existing.total > 0
                ? Math.round(((existing.present + existing.late + existing.excused) / existing.total) * 100)
                : 0,
            };
          }
          return {
            batch_id: bId,
            batch_name: enr?.batch_name ?? 'Batch',
            total: 0, present: 0, absent: 0, late: 0, excused: 0, percentage: 0,
          };
        });

        setBatchAttendance(stats);
        setAttendanceRecordsByBatch(recordsMap);
        if (stats.length > 0) setSelectedBatchId(prev => (prev && stats.some(s => s.batch_id === prev)) ? prev : stats[0].batch_id);
      }
    } catch (err) {
      // fail silently on the dashboard
    } finally {
      setAttendanceLoading(false);
    }
  };

  const fetchFees = async () => {
    try {
      setFeesLoading(true);
      const res = await apiClient.get<StudentFeesResponse>('/v1/student-fees');
      if (res.data.success) setFeesDetails(res.data.data.fees_details);
    } catch (err) {
      // fail silently on the dashboard
    } finally {
      setFeesLoading(false);
    }
  };

  /* ── Calendar derived data ─────────────────────────────────────────── */
  const [selectedDayKey, setSelectedDayKey] = useState<string | null>(null);

  const dayRecordMap = useMemo(() => {
    const map: Record<string, AttendanceRecordItem> = {};
    if (selectedBatchId && attendanceRecordsByBatch[selectedBatchId]) {
      for (const rec of attendanceRecordsByBatch[selectedBatchId]) {
        map[toDateKey(rec.attendance_date)] = rec;
      }
    }
    return map;
  }, [selectedBatchId, attendanceRecordsByBatch]);

  // Stats shown next to the calendar (donut + counts) reflect only the month
  // currently being viewed — flipping months should change these numbers.
  const monthStat: BatchAttendanceStat | null = useMemo(() => {
    if (!selectedBatchId) return null;
    const year = calendarDate.getFullYear();
    const month = calendarDate.getMonth();
    const counts: Record<AttendanceStatus, number> = { present: 0, absent: 0, late: 0, excused: 0 };
    let total = 0;

    for (const key in dayRecordMap) {
      const [y, m] = key.split('-').map(Number);
      if (y === year && m === month + 1) {
        counts[dayRecordMap[key].status] += 1;
        total += 1;
      }
    }

    const batchMeta = batchAttendance.find(s => s.batch_id === selectedBatchId);
    const percentage = total > 0 ? Math.round(((counts.present + counts.late + counts.excused) / total) * 100) : 0;

    return {
      batch_id: selectedBatchId,
      batch_name: batchMeta?.batch_name ?? 'Batch',
      total,
      present: counts.present,
      absent: counts.absent,
      late: counts.late,
      excused: counts.excused,
      percentage,
    };
  }, [selectedBatchId, dayRecordMap, calendarDate, batchAttendance]);

  const feesSummary = useMemo(() => {
    const base = feesDetails.reduce(
      (acc, fd) => ({
        totalFee: acc.totalFee + (fd.fees?.total_fee ?? 0),
        totalBase: acc.totalBase + (fd.fees?.base_fee ?? 0),
        totalAdditional: acc.totalAdditional + (fd.fees?.additional_fee ?? 0),
        totalPaid: acc.totalPaid + (fd.fees?.total_paid ?? 0),
        balanceDue: acc.balanceDue + (fd.fees?.balance_due ?? 0),
        paymentCount: acc.paymentCount + (fd.fees?.payment_count ?? 0),
      }),
      { totalFee: 0, totalBase: 0, totalAdditional: 0, totalPaid: 0, balanceDue: 0, paymentCount: 0 }
    );

    const fullyPaidCount = feesDetails.filter(fd => fd.fees?.payment_status === 'fully_paid').length;
    const partialCount = feesDetails.filter(fd => fd.fees?.payment_status === 'partial').length;
    const notPaidCount = feesDetails.filter(fd => fd.fees?.payment_status === 'not_paid').length;

    const lastPaymentDate = feesDetails
      .map(fd => fd.fees?.last_payment_date)
      .filter((d): d is string => !!d)
      .sort((a, b) => new Date(b).getTime() - new Date(a).getTime())[0] ?? null;

    const overallPct = base.totalFee > 0 ? Math.round((base.totalPaid / base.totalFee) * 100) : 0;

    return { ...base, fullyPaidCount, partialCount, notPaidCount, lastPaymentDate, overallPct };
  }, [feesDetails]);

  const calendarCells = useMemo(() => {
    const year = calendarDate.getFullYear();
    const month = calendarDate.getMonth();
    const firstWeekday = new Date(year, month, 1).getDay();
    const daysInMonth = new Date(year, month + 1, 0).getDate();
    const todayKey = toDateKey(new Date().toISOString());

    const cells: { day: number | null; key: string | null; status: AttendanceStatus | null; isToday: boolean }[] = [];
    for (let i = 0; i < firstWeekday; i++) cells.push({ day: null, key: null, status: null, isToday: false });
    for (let d = 1; d <= daysInMonth; d++) {
      const key = `${year}-${String(month + 1).padStart(2, '0')}-${String(d).padStart(2, '0')}`;
      cells.push({ day: d, key, status: dayRecordMap[key]?.status ?? null, isToday: key === todayKey });
    }
    return cells;
  }, [calendarDate, dayRecordMap]);

  const selectedDayRecord = selectedDayKey ? dayRecordMap[selectedDayKey] ?? null : null;

  const shiftMonth = (delta: number) => {
    setCalendarDate(prev => new Date(prev.getFullYear(), prev.getMonth() + delta, 1));
    setSelectedDayKey(null);
  };

  const handleDayClick = (key: string | null) => {
    if (!key) return;
    setSelectedDayKey(prev => (prev === key ? null : key));
  };

  /* ── Guard renders ─────────────────────────────────────────────────── */
  if (profileLoading) {
    return (
      <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '400px', flexDirection: 'column', gap: '12px' }}>
        <ProgressSpinner style={{ width: '44px', height: '44px' }} strokeWidth="3" animationDuration=".8s" />
        <p style={{ color: C.navy, fontSize: '0.9rem', fontWeight: '500' }}>Loading profile…</p>
      </div>
    );
  }

  if (!branchId) {
    return (
      <div style={{ padding: '20px' }}>
        <Message severity="warn" text="Branch information is missing. Please contact support." style={{ fontSize: '0.88rem' }} />
      </div>
    );
  }

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: '16px' }}>
      <style>{`
        .db-feed-item { transition: background 0.12s ease; cursor: pointer; }
        .db-feed-item:hover { background: rgba(14,34,93,0.025) !important; }
        .db-view-all { transition: opacity 0.15s ease; }
        .db-view-all:hover { opacity: 0.7; }
        .db-enroll-scroll { display: grid; grid-auto-flow: column; grid-template-rows: repeat(2, auto); grid-auto-columns: 100%; gap: 14px; overflow-x: auto; scroll-behavior: smooth; padding-bottom: 4px; scrollbar-width: none; }
        .db-enroll-scroll::-webkit-scrollbar { display: none; }
        .db-enroll-nav-btn { transition: all 0.15s ease; }
        .db-enroll-nav-btn:hover { background: ${C.navyHover} !important; border-color: rgba(14,34,93,0.25) !important; }
        .db-batch-tab { transition: all 0.15s ease; white-space: nowrap; }
        .db-batch-tab:hover { transform: translateY(-1px); }
        .db-cal-nav-btn { transition: all 0.15s ease; }
        .db-cal-nav-btn:hover { background: ${C.navyHover} !important; border-color: rgba(14,34,93,0.25) !important; }
        .db-cal-cell { transition: transform 0.1s ease, box-shadow 0.1s ease; cursor: pointer; }
        .db-cal-cell:hover { transform: scale(1.1); box-shadow: 0 1px 4px rgba(14,34,93,0.18); }
        .db-cal-cell:active { transform: scale(0.96); }
        .db-day-detail { animation: db-fade-in 0.15s ease; }
        .db-fee-expand-btn { transition: all 0.15s ease; }
        .db-fee-expand-btn:hover { background: ${C.navyHover} !important; }
        .db-fee-detail-row { animation: db-fade-in 0.15s ease; }
        .db-stat-tile { transition: transform 0.15s ease, box-shadow 0.15s ease; }
        .db-stat-tile:hover { transform: translateY(-2px); box-shadow: 0 4px 10px rgba(14,34,93,0.08); }
        @keyframes db-fade-in { from { opacity: 0; transform: translateY(-2px); } to { opacity: 1; transform: translateY(0); } }
      `}</style>

      {/* ── Greeting Banner ──────────────────────────────────────────── */}
      <div style={{
        position: 'relative',
        background: `linear-gradient(135deg, ${C.navy} 0%, ${C.navyMid} 55%, ${C.navyDark} 100%)`,
        borderRadius: '16px',
        padding: '26px 28px',
        overflow: 'hidden',
        boxShadow: '0 4px 16px rgba(14,34,93,0.18)',
      }}>
        <div style={{ position: 'absolute', top: '-60px', right: '-40px', width: '200px', height: '200px', background: 'rgba(251,178,31,0.12)', borderRadius: '50%', filter: 'blur(50px)', pointerEvents: 'none' }} />
        <div style={{ position: 'absolute', bottom: '-40px', right: '18%', width: '110px', height: '110px', background: 'rgba(251,178,31,0.06)', borderRadius: '50%', filter: 'blur(35px)', pointerEvents: 'none' }} />

        <div style={{ position: 'relative', zIndex: 1, display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: '20px', flexWrap: 'wrap' }}>
          <div>
            <p style={{ margin: '0 0 4px 0', fontSize: '0.95rem', color: 'rgba(255,255,255,0.75)', fontWeight: '500' }}>
              {getGreeting()}, <span style={{ fontSize: '1.05rem' }}>👋</span>
            </p>
            <h1 style={{ margin: '0 0 8px 0', fontSize: '1.5rem', fontWeight: '800', color: C.white, letterSpacing: '-0.4px' }}>
              {firstName || 'Student'}!
            </h1>
            <p style={{ margin: 0, fontSize: '0.82rem', color: 'rgba(255,255,255,0.55)', fontWeight: '500' }}>
              Stay updated with your academic journey.
            </p>
          </div>

          {/* Profile Picture */}
          <div style={{
            width: '68px', height: '68px', flexShrink: 0, borderRadius: '18px',
            border: `2px solid ${C.goldBorder}`, boxShadow: '0 4px 14px rgba(0,0,0,0.25)',
            overflow: 'hidden', background: 'rgba(255,255,255,0.08)',
            display: 'flex', alignItems: 'center', justifyContent: 'center',
          }}>
            {profilePicUrl ? (
              <img
                src={profilePicUrl}
                alt={firstName || 'Profile'}
                style={{ width: '100%', height: '100%', objectFit: 'cover' }}
                onError={e => { (e.currentTarget as HTMLImageElement).style.display = 'none'; }}
              />
            ) : (
              <span style={{ fontSize: '1.3rem', fontWeight: '800', color: C.gold, letterSpacing: '0.5px' }}>
                {initials}
              </span>
            )}
          </div>
        </div>
      </div>

      {error && <Message severity="error" text={error} style={{ width: '100%', fontSize: '0.88rem' }} />}

      {/* ── Enrollments + Attendance — side by side ─────────────── */}
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(380px, 1fr))', gap: '16px' }}>

      {/* Enrollments Card */}
        <div style={{ background: C.white, borderRadius: '14px', border: `1px solid ${C.border}`, boxShadow: '0 1px 4px rgba(14,34,93,0.06)', padding: '16px 18px' }}>
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '14px' }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
              <div style={{ width: '3px', height: '16px', background: `linear-gradient(180deg, ${C.gold} 0%, #e09a00 100%)`, borderRadius: '3px' }} />
              <span style={{ fontSize: '0.9rem', fontWeight: '700', color: C.navy }}>My Enrollments</span>
              <span style={{ padding: '2px 9px', background: C.navyHover, borderRadius: '20px', fontSize: '0.7rem', fontWeight: '700', color: C.navy }}>
                {enrollments.length}
              </span>
            </div>

            {enrollments.length > 2 && (
              <div style={{ display: 'flex', gap: '6px' }}>
                <button
                  className="db-enroll-nav-btn"
                  onClick={() => scrollCarousel('left')}
                  style={{ width: '28px', height: '28px', background: 'transparent', border: `1px solid ${C.border}`, borderRadius: '7px', cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center', color: C.navy }}
                >
                  <i className="pi pi-chevron-left" style={{ fontSize: '0.65rem' }} />
                </button>
                <button
                  className="db-enroll-nav-btn"
                  onClick={() => scrollCarousel('right')}
                  style={{ width: '28px', height: '28px', background: 'transparent', border: `1px solid ${C.border}`, borderRadius: '7px', cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center', color: C.navy }}
                >
                  <i className="pi pi-chevron-right" style={{ fontSize: '0.65rem' }} />
                </button>
              </div>
            )}
          </div>

          {enrollments.length === 0 ? (
            <div style={{ padding: '30px 10px', textAlign: 'center' }}>
              <i className="pi pi-book" style={{ fontSize: '1.8rem', color: C.navy, opacity: 0.18 }} />
              <p style={{ marginTop: '10px', color: C.muted, fontSize: '0.85rem' }}>No enrollments yet</p>
            </div>
          ) : (
            <div className="db-enroll-scroll" ref={carouselRef}>
              {enrollments.map(enr => {
                const duration = [
                  formatMonthYear(enr.start_month, enr.start_year),
                  formatMonthYear(enr.end_month, enr.end_year),
                ].filter(Boolean).join(' – ');

                return (
                  <div
                    key={enr.enrollment_id}
                    style={{
                      borderRadius: '12px', border: `1px solid ${C.border}`,
                      background: `linear-gradient(160deg, ${C.white} 0%, #fbfbfd 100%)`,
                      padding: '14px', position: 'relative', overflow: 'hidden',
                    }}
                  >
                    <div style={{ position: 'absolute', top: 0, left: 0, right: 0, height: '3px', background: `linear-gradient(90deg, ${C.navy}, ${C.gold})` }} />

                    <div style={{ display: 'flex', alignItems: 'flex-start', gap: '10px', marginBottom: '10px' }}>
                      <div style={{ width: '34px', height: '34px', flexShrink: 0, borderRadius: '9px', background: C.navyHover, border: '1px solid rgba(14,34,93,0.15)', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                        <i className="pi pi-book" style={{ fontSize: '0.85rem', color: C.navy }} />
                      </div>
                      <div style={{ flex: 1, minWidth: 0 }}>
                        <p style={{ margin: '0 0 2px 0', fontSize: '0.82rem', fontWeight: '700', color: C.navy, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                          {enr.course_name ?? enr.course_title ?? 'Course'}
                        </p>
                        {enr.enrollment_no && (
                          <span style={{ fontSize: '0.68rem', color: C.muted, fontFamily: 'monospace' }}>
                            {enr.enrollment_no}
                          </span>
                        )}
                      </div>
                    </div>

                    {enr.batch_name && (
                      <div style={{ display: 'inline-flex', alignItems: 'center', gap: '5px', padding: '3px 9px', background: C.goldDim, border: `1px solid ${C.goldBorder}`, borderRadius: '20px', fontSize: '0.68rem', fontWeight: '700', color: '#a06000', marginBottom: '10px' }}>
                        <i className="pi pi-users" style={{ fontSize: '0.6rem' }} />
                        {enr.batch_name}
                      </div>
                    )}

                    <div style={{ display: 'flex', flexDirection: 'column', gap: '5px', paddingTop: '8px', borderTop: '1px solid #f3f4f6' }}>
                      {enr.batch_time && (
                        <div style={{ display: 'flex', alignItems: 'center', gap: '6px', fontSize: '0.72rem', color: C.sub }}>
                          <i className="pi pi-clock" style={{ fontSize: '0.65rem', color: C.muted }} />
                          {enr.batch_time}
                        </div>
                      )}
                      {duration && (
                        <div style={{ display: 'flex', alignItems: 'center', gap: '6px', fontSize: '0.72rem', color: C.sub }}>
                          <i className="pi pi-calendar" style={{ fontSize: '0.65rem', color: C.muted }} />
                          {duration}
                        </div>
                      )}
                      {enr.date_of_admission && (
                        <div style={{ display: 'flex', alignItems: 'center', gap: '6px', fontSize: '0.72rem', color: C.sub }}>
                          <i className="pi pi-verified" style={{ fontSize: '0.65rem', color: C.muted }} />
                          Admitted {formatDate(enr.date_of_admission)}
                        </div>
                      )}
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </div>

      {/* ── Attendance — compact month calendar + donut stats ────────── */}
      {(attendanceLoading || batchAttendance.length > 0) && (
        <div style={{ background: C.white, borderRadius: '14px', border: `1px solid ${C.border}`, boxShadow: '0 1px 4px rgba(14,34,93,0.06)', padding: '14px 16px' }}>
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: '8px', marginBottom: '12px' }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
              <div style={{ width: '3px', height: '16px', background: `linear-gradient(180deg, ${C.gold} 0%, #e09a00 100%)`, borderRadius: '3px' }} />
              <span style={{ fontSize: '0.9rem', fontWeight: '700', color: C.navy }}>Attendance</span>
            </div>

            {/* Batch tabs */}
            {batchAttendance.length > 1 && (
              <div style={{ display: 'flex', gap: '6px', flexWrap: 'wrap' }}>
                {batchAttendance.map(stat => {
                  const active = stat.batch_id === selectedBatchId;
                  return (
                    <button
                      key={stat.batch_id}
                      className="db-batch-tab"
                      onClick={() => { setSelectedBatchId(stat.batch_id); setSelectedDayKey(null); }}
                      style={{
                        padding: '3px 11px', borderRadius: '20px', fontSize: '0.7rem', fontWeight: '700', cursor: 'pointer',
                        border: active ? 'none' : `1px solid ${C.border}`,
                        background: active ? C.navy : 'transparent',
                        color: active ? C.gold : C.sub,
                        boxShadow: active ? '0 2px 6px rgba(14,34,93,0.2)' : 'none',
                      }}
                    >
                      {stat.batch_name}
                    </button>
                  );
                })}
              </div>
            )}
          </div>

          {attendanceLoading ? (
            <div style={{ display: 'flex', justifyContent: 'center', padding: '30px 0' }}>
              <ProgressSpinner style={{ width: '32px', height: '32px' }} strokeWidth="4" animationDuration=".8s" />
            </div>
          ) : !selectedBatchId ? (
            <div style={{ padding: '28px 20px', textAlign: 'center' }}>
              <i className="pi pi-calendar" style={{ fontSize: '1.6rem', color: C.navy, opacity: 0.18 }} />
              <p style={{ marginTop: '8px', color: C.muted, fontSize: '0.83rem' }}>No attendance records yet</p>
            </div>
          ) : (
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(210px, 1fr))', gap: '18px', alignItems: 'start' }}>

              {/* ── Compact Calendar ─────────────────────────────────── */}
              <div>
                <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '8px' }}>
                  <button
                    className="db-cal-nav-btn"
                    onClick={() => shiftMonth(-1)}
                    style={{ width: '22px', height: '22px', background: 'transparent', border: `1px solid ${C.border}`, borderRadius: '6px', cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center', color: C.navy }}
                  >
                    <i className="pi pi-chevron-left" style={{ fontSize: '0.58rem' }} />
                  </button>
                  <span style={{ fontSize: '0.78rem', fontWeight: '700', color: C.navy }}>
                    {MONTH_NAMES[calendarDate.getMonth()]} {calendarDate.getFullYear()}
                  </span>
                  <button
                    className="db-cal-nav-btn"
                    onClick={() => shiftMonth(1)}
                    style={{ width: '22px', height: '22px', background: 'transparent', border: `1px solid ${C.border}`, borderRadius: '6px', cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center', color: C.navy }}
                  >
                    <i className="pi pi-chevron-right" style={{ fontSize: '0.58rem' }} />
                  </button>
                </div>

                <div style={{ display: 'grid', gridTemplateColumns: 'repeat(7, 1fr)', gap: '3px', marginBottom: '4px' }}>
                  {WEEKDAY_SHORT.map(w => (
                    <div key={w} style={{ textAlign: 'center', fontSize: '0.58rem', fontWeight: '700', color: C.muted, textTransform: 'uppercase', letterSpacing: '0.3px' }}>
                      {w[0]}
                    </div>
                  ))}
                </div>

                <div style={{ display: 'grid', gridTemplateColumns: 'repeat(7, 1fr)', gap: '3px' }}>
                  {calendarCells.map((cell, idx) => {
                    if (cell.day === null) return <div key={`b${idx}`} />;
                    const meta = cell.status ? STATUS_META[cell.status] : null;
                    const isSelected = cell.key === selectedDayKey;
                    return (
                      <button
                        key={cell.key}
                        type="button"
                        className="db-cal-cell"
                        onClick={() => handleDayClick(cell.key)}
                        title={cell.status ? STATUS_META[cell.status].label : 'No class'}
                        style={{
                          aspectRatio: '1', display: 'flex', alignItems: 'center', justifyContent: 'center',
                          borderRadius: '6px', fontSize: '0.66rem', fontWeight: '700', fontFamily: 'inherit',
                          background: meta ? `${meta.color}1a` : '#f7f8fa',
                          color: meta ? meta.color : C.muted,
                          border: isSelected ? `1.5px solid ${C.navy}` : cell.isToday ? `1.5px solid ${C.goldBorder}` : '1px solid transparent',
                          padding: 0,
                        }}
                      >
                        {cell.day}
                      </button>
                    );
                  })}
                </div>

                {/* Legend */}
                <div style={{ display: 'flex', gap: '10px', flexWrap: 'wrap', marginTop: '10px', paddingTop: '8px', borderTop: '1px solid #f3f4f6' }}>
                  {(Object.keys(STATUS_META) as AttendanceStatus[]).map(key => (
                    <div key={key} style={{ display: 'flex', alignItems: 'center', gap: '4px', fontSize: '0.65rem', color: C.sub, fontWeight: '600' }}>
                      <span style={{ width: '7px', height: '7px', borderRadius: '2px', background: STATUS_META[key].color, display: 'inline-block' }} />
                      {STATUS_META[key].label}
                    </div>
                  ))}
                </div>

                {/* Selected day detail */}
                {selectedDayKey && (
                  <div
                    className="db-day-detail"
                    style={{
                      marginTop: '10px', padding: '9px 10px', borderRadius: '9px',
                      background: selectedDayRecord ? `${STATUS_META[selectedDayRecord.status].color}0d` : '#f7f8fa',
                      border: `1px solid ${selectedDayRecord ? `${STATUS_META[selectedDayRecord.status].color}40` : C.border}`,
                    }}
                  >
                    <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: '8px' }}>
                      <span style={{ fontSize: '0.72rem', fontWeight: '700', color: C.navy }}>
                        {formatDateKeyLong(selectedDayKey)}
                      </span>
                      {selectedDayRecord ? (
                        <span style={{
                          fontSize: '0.62rem', fontWeight: '700', padding: '2px 8px', borderRadius: '20px',
                          color: STATUS_META[selectedDayRecord.status].color,
                          background: `${STATUS_META[selectedDayRecord.status].color}1a`,
                        }}>
                          {STATUS_META[selectedDayRecord.status].label}
                        </span>
                      ) : (
                        <span style={{ fontSize: '0.62rem', fontWeight: '700', padding: '2px 8px', borderRadius: '20px', color: C.muted, background: '#eceef1' }}>
                          No class
                        </span>
                      )}
                    </div>
                    {selectedDayRecord?.topic && (
                      <p style={{ margin: '5px 0 0 0', fontSize: '0.72rem', color: C.sub }}>
                        {selectedDayRecord.topic}
                      </p>
                    )}
                  </div>
                )}
              </div>

              {/* ── Donut + Stats ────────────────────────────────────── */}
              <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: '12px' }}>
                <div style={{ position: 'relative', width: '130px', height: '130px' }}>
                  <DonutChart stats={monthStat} size={130} />
                  <div style={{ position: 'absolute', inset: 0, display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center' }}>
                    <span style={{ fontSize: '1.3rem', fontWeight: '800', color: C.navy, letterSpacing: '-0.5px' }}>
                      {monthStat?.percentage ?? 0}%
                    </span>
                    <span style={{ fontSize: '0.58rem', color: C.muted, fontWeight: '700', textTransform: 'uppercase', letterSpacing: '0.3px' }}>
                      {MONTH_SHORT[calendarDate.getMonth()]} {calendarDate.getFullYear()}
                    </span>
                  </div>
                </div>

                <div style={{ width: '100%', display: 'flex', flexDirection: 'column', gap: '6px' }}>
                  {(Object.keys(STATUS_META) as AttendanceStatus[]).map(key => {
                    const count = monthStat ? (monthStat as any)[key] as number : 0;
                    const pct = monthStat && monthStat.total > 0 ? Math.round((count / monthStat.total) * 100) : 0;
                    return (
                      <div key={key} style={{ display: 'flex', alignItems: 'center', gap: '7px' }}>
                        <span style={{ width: '7px', height: '7px', borderRadius: '2px', background: STATUS_META[key].color, flexShrink: 0 }} />
                        <span style={{ fontSize: '0.72rem', color: C.sub, fontWeight: '600', flex: 1 }}>{STATUS_META[key].label}</span>
                        <span style={{ fontSize: '0.72rem', color: C.navy, fontWeight: '700' }}>{count}</span>
                        <span style={{ fontSize: '0.64rem', color: C.muted, fontWeight: '600', minWidth: '30px', textAlign: 'right' }}>{pct}%</span>
                      </div>
                    );
                  })}
                  <div style={{ display: 'flex', alignItems: 'center', gap: '7px', paddingTop: '5px', marginTop: '1px', borderTop: '1px solid #f3f4f6' }}>
                    <span style={{ fontSize: '0.72rem', color: C.navy, fontWeight: '700', flex: 1 }}>Classes This Month</span>
                    <span style={{ fontSize: '0.72rem', color: C.navy, fontWeight: '800' }}>{monthStat?.total ?? 0}</span>
                  </div>
                </div>
              </div>
            </div>
          )}
        </div>
      )}

      </div>


      {/* ── Notice Board + Announcements + Assignments ──────────── */}
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(300px, 1fr))', gap: '16px' }}>

        {/* Notice Board Card */}
        <div style={{ background: C.white, borderRadius: '14px', border: `1px solid ${C.border}`, boxShadow: '0 1px 4px rgba(14,34,93,0.06)', overflow: 'hidden' }}>
          <div style={{ padding: '14px 18px', borderBottom: `1px solid ${C.border}`, display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
              <div style={{ width: '3px', height: '16px', background: `linear-gradient(180deg, ${C.gold} 0%, #e09a00 100%)`, borderRadius: '3px' }} />
              <span style={{ fontSize: '0.9rem', fontWeight: '700', color: C.navy }}>Notice Board</span>
            </div>
            <a className="db-view-all" onClick={() => navigate('/notices')} style={{ fontSize: '0.76rem', fontWeight: '700', color: C.navy, cursor: 'pointer', textDecoration: 'none' }}>
              View All
            </a>
          </div>

          {loading ? (
            <div style={{ display: 'flex', justifyContent: 'center', padding: '40px 0' }}>
              <ProgressSpinner style={{ width: '30px', height: '30px' }} strokeWidth="4" animationDuration=".8s" />
            </div>
          ) : notices.length === 0 ? (
            <div style={{ padding: '36px 20px', textAlign: 'center' }}>
              <i className="pi pi-bell" style={{ fontSize: '1.8rem', color: C.navy, opacity: 0.18 }} />
              <p style={{ marginTop: '10px', color: C.muted, fontSize: '0.85rem' }}>No notices yet</p>
            </div>
          ) : (
            <div>
              {notices.map((n, idx) => (
                <div
                  key={n.notice_id}
                  className="db-feed-item"
                  onClick={() => navigate('/notices')}
                  style={{ display: 'flex', gap: '12px', padding: '13px 18px', borderTop: idx === 0 ? 'none' : `1px solid #f3f4f6` }}
                >
                  <div style={{ width: '34px', height: '34px', flexShrink: 0, borderRadius: '10px', background: C.navyHover, border: '1px solid rgba(14,34,93,0.15)', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                    <i className="pi pi-bell" style={{ fontSize: '0.85rem', color: C.navy }} />
                  </div>
                  <div style={{ flex: 1, minWidth: 0 }}>
                    <p style={{ margin: '0 0 3px 0', fontSize: '0.83rem', fontWeight: '700', color: C.body, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                      {n.notice_title}
                    </p>
                    <p style={{ margin: '0 0 5px 0', fontSize: '0.78rem', color: C.sub, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                      {n.notice_body}
                    </p>
                    <span style={{ fontSize: '0.73rem', color: C.navy, fontWeight: '600' }}>{formatDate(n.created_at)}</span>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>

        {/* Announcements Card */}
        <div style={{ background: C.white, borderRadius: '14px', border: `1px solid ${C.border}`, boxShadow: '0 1px 4px rgba(14,34,93,0.06)', overflow: 'hidden' }}>
          <div style={{ padding: '14px 18px', borderBottom: `1px solid ${C.border}`, display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
              <div style={{ width: '3px', height: '16px', background: `linear-gradient(180deg, ${C.gold} 0%, #e09a00 100%)`, borderRadius: '3px' }} />
              <span style={{ fontSize: '0.9rem', fontWeight: '700', color: C.navy }}>Announcements</span>
            </div>
            <a className="db-view-all" onClick={() => navigate('/announcements')} style={{ fontSize: '0.76rem', fontWeight: '700', color: C.navy, cursor: 'pointer', textDecoration: 'none' }}>
              View All
            </a>
          </div>

          {loading ? (
            <div style={{ display: 'flex', justifyContent: 'center', padding: '40px 0' }}>
              <ProgressSpinner style={{ width: '30px', height: '30px' }} strokeWidth="4" animationDuration=".8s" />
            </div>
          ) : announcements.length === 0 ? (
            <div style={{ padding: '36px 20px', textAlign: 'center' }}>
              <i className="pi pi-megaphone" style={{ fontSize: '1.8rem', color: C.navy, opacity: 0.18 }} />
              <p style={{ marginTop: '10px', color: C.muted, fontSize: '0.85rem' }}>No announcements yet</p>
            </div>
          ) : (
            <div>
              {announcements.map((a, idx) => (
                <div
                  key={a.announcement_id}
                  className="db-feed-item"
                  onClick={() => navigate('/announcements')}
                  style={{ display: 'flex', gap: '12px', padding: '13px 18px', borderTop: idx === 0 ? 'none' : `1px solid #f3f4f6` }}
                >
                  <div style={{ width: '34px', height: '34px', flexShrink: 0, borderRadius: '10px', background: C.goldDim, border: `1px solid ${C.goldBorder}`, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                    <i className="pi pi-megaphone" style={{ fontSize: '0.85rem', color: '#a06000' }} />
                  </div>
                  <div style={{ flex: 1, minWidth: 0 }}>
                    <p style={{ margin: '0 0 3px 0', fontSize: '0.83rem', fontWeight: '700', color: C.body, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                      {a.announcement_title}
                    </p>
                    <p style={{ margin: '0 0 5px 0', fontSize: '0.78rem', color: C.sub, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                      {a.announcement_body}
                    </p>
                    <span style={{ fontSize: '0.73rem', color: C.navy, fontWeight: '600' }}>{formatDate(a.created_at)}</span>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>

        {/* Assignments Card */}
        <div style={{ background: C.white, borderRadius: '14px', border: `1px solid ${C.border}`, boxShadow: '0 1px 4px rgba(14,34,93,0.06)', overflow: 'hidden' }}>
          <div style={{ padding: '14px 18px', borderBottom: `1px solid ${C.border}`, display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
              <div style={{ width: '3px', height: '16px', background: `linear-gradient(180deg, ${C.gold} 0%, #e09a00 100%)`, borderRadius: '3px' }} />
              <span style={{ fontSize: '0.9rem', fontWeight: '700', color: C.navy }}>Assignments</span>
            </div>
            <a className="db-view-all" onClick={() => navigate('/assignments')} style={{ fontSize: '0.76rem', fontWeight: '700', color: C.navy, cursor: 'pointer', textDecoration: 'none' }}>
              View All
            </a>
          </div>

          {assignmentsLoading ? (
            <div style={{ display: 'flex', justifyContent: 'center', padding: '40px 0' }}>
              <ProgressSpinner style={{ width: '30px', height: '30px' }} strokeWidth="4" animationDuration=".8s" />
            </div>
          ) : assignments.length === 0 ? (
            <div style={{ padding: '36px 20px', textAlign: 'center' }}>
              <i className="pi pi-file-edit" style={{ fontSize: '1.8rem', color: C.navy, opacity: 0.18 }} />
              <p style={{ marginTop: '10px', color: C.muted, fontSize: '0.85rem' }}>No assignments yet</p>
            </div>
          ) : (
            <div>
              {assignments.map((asn, idx) => (
                <div
                  key={asn.assignment_id}
                  className="db-feed-item"
                  onClick={() => navigate('/assignments')}
                  style={{ display: 'flex', gap: '12px', padding: '13px 18px', borderTop: idx === 0 ? 'none' : `1px solid #f3f4f6` }}
                >
                  <div style={{ width: '34px', height: '34px', flexShrink: 0, borderRadius: '10px', background: C.greenDim, border: `1px solid ${C.greenBorder}`, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                    <i className="pi pi-file-edit" style={{ fontSize: '0.85rem', color: C.green }} />
                  </div>
                  <div style={{ flex: 1, minWidth: 0 }}>
                    <p style={{ margin: '0 0 3px 0', fontSize: '0.83rem', fontWeight: '700', color: C.body, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                      {asn.assignment_title}
                    </p>
                    <p style={{ margin: '0 0 5px 0', fontSize: '0.78rem', color: C.sub, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                      {asn.batch_name ?? 'All Batches'}
                    </p>
                    <span style={{ fontSize: '0.73rem', color: asn.submission_date ? '#b91c1c' : C.navy, fontWeight: '600' }}>
                      {asn.submission_date ? `Due ${formatDate(asn.submission_date)}` : formatDate(asn.created_at)}
                    </span>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>

      {/* ── Fees — full width, detailed, with bar chart ──────────────── */}
      <div style={{ background: C.white, borderRadius: '14px', border: `1px solid ${C.border}`, boxShadow: '0 1px 4px rgba(14,34,93,0.06)', overflow: 'hidden' }}>
        <div style={{ padding: '14px 18px', borderBottom: `1px solid ${C.border}`, display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: '8px' }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
            <div style={{ width: '3px', height: '16px', background: `linear-gradient(180deg, ${C.gold} 0%, #e09a00 100%)`, borderRadius: '3px' }} />
            <span style={{ fontSize: '0.9rem', fontWeight: '700', color: C.navy }}>Fees</span>
            {feesDetails.length > 0 && (
              <span style={{ padding: '2px 9px', background: C.navyHover, borderRadius: '20px', fontSize: '0.7rem', fontWeight: '700', color: C.navy }}>
                {feesDetails.length} enrollment{feesDetails.length > 1 ? 's' : ''}
              </span>
            )}
          </div>
          <a className="db-view-all" onClick={() => navigate('/fees')} style={{ fontSize: '0.76rem', fontWeight: '700', color: C.navy, cursor: 'pointer', textDecoration: 'none' }}>
            View All
          </a>
        </div>

        {feesLoading ? (
          <div style={{ display: 'flex', justifyContent: 'center', padding: '50px 0' }}>
            <ProgressSpinner style={{ width: '32px', height: '32px' }} strokeWidth="4" animationDuration=".8s" />
          </div>
        ) : feesDetails.length === 0 ? (
          <div style={{ padding: '40px 20px', textAlign: 'center' }}>
            <i className="pi pi-wallet" style={{ fontSize: '1.8rem', color: C.navy, opacity: 0.18 }} />
            <p style={{ marginTop: '10px', color: C.muted, fontSize: '0.85rem' }}>No fee records yet</p>
          </div>
        ) : (
          <div style={{ padding: '18px' }}>

            {/* Overview stat tiles */}
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(140px, 1fr))', gap: '10px', marginBottom: '20px' }}>
              <div className="db-stat-tile" style={{ padding: '12px 14px', borderRadius: '11px', border: `1px solid ${C.border}`, background: '#fbfbfd' }}>
                <p style={{ margin: '0 0 4px 0', fontSize: '0.64rem', color: C.muted, fontWeight: '700', textTransform: 'uppercase', letterSpacing: '0.3px' }}>Total Fee</p>
                <p style={{ margin: 0, fontSize: '1.05rem', color: C.navy, fontWeight: '800' }}>{formatCurrency(feesSummary.totalFee)}</p>
              </div>
              <div className="db-stat-tile" style={{ padding: '12px 14px', borderRadius: '11px', border: `1px solid ${C.greenBorder}`, background: C.greenDim }}>
                <p style={{ margin: '0 0 4px 0', fontSize: '0.64rem', color: '#0e7a3c', fontWeight: '700', textTransform: 'uppercase', letterSpacing: '0.3px' }}>Total Paid</p>
                <p style={{ margin: 0, fontSize: '1.05rem', color: C.green, fontWeight: '800' }}>{formatCurrency(feesSummary.totalPaid)}</p>
              </div>
              <div className="db-stat-tile" style={{ padding: '12px 14px', borderRadius: '11px', border: `1px solid ${feesSummary.balanceDue > 0 ? C.redBorder : C.border}`, background: feesSummary.balanceDue > 0 ? C.redDim : '#fbfbfd' }}>
                <p style={{ margin: '0 0 4px 0', fontSize: '0.64rem', color: feesSummary.balanceDue > 0 ? '#a02020' : C.muted, fontWeight: '700', textTransform: 'uppercase', letterSpacing: '0.3px' }}>Balance Due</p>
                <p style={{ margin: 0, fontSize: '1.05rem', color: feesSummary.balanceDue > 0 ? C.red : C.navy, fontWeight: '800' }}>{formatCurrency(feesSummary.balanceDue)}</p>
              </div>
              <div className="db-stat-tile" style={{ padding: '12px 14px', borderRadius: '11px', border: `1px solid ${C.goldBorder}`, background: C.goldDim }}>
                <p style={{ margin: '0 0 4px 0', fontSize: '0.64rem', color: '#a06000', fontWeight: '700', textTransform: 'uppercase', letterSpacing: '0.3px' }}>Overall Paid</p>
                <p style={{ margin: 0, fontSize: '1.05rem', color: '#a06000', fontWeight: '800' }}>{feesSummary.overallPct}%</p>
              </div>
              <div className="db-stat-tile" style={{ padding: '12px 14px', borderRadius: '11px', border: `1px solid ${C.border}`, background: '#fbfbfd' }}>
                <p style={{ margin: '0 0 4px 0', fontSize: '0.64rem', color: C.muted, fontWeight: '700', textTransform: 'uppercase', letterSpacing: '0.3px' }}>Payments Made</p>
                <p style={{ margin: 0, fontSize: '1.05rem', color: C.navy, fontWeight: '800' }}>{feesSummary.paymentCount}</p>
              </div>
              <div className="db-stat-tile" style={{ padding: '12px 14px', borderRadius: '11px', border: `1px solid ${C.border}`, background: '#fbfbfd' }}>
                <p style={{ margin: '0 0 4px 0', fontSize: '0.64rem', color: C.muted, fontWeight: '700', textTransform: 'uppercase', letterSpacing: '0.3px' }}>Last Payment</p>
                <p style={{ margin: 0, fontSize: '0.92rem', color: C.navy, fontWeight: '800' }}>{formatDate(feesSummary.lastPaymentDate)}</p>
              </div>
            </div>

            {/* Status breakdown chips */}
            <div style={{ display: 'flex', gap: '8px', flexWrap: 'wrap', marginBottom: '20px' }}>
              <span style={{ display: 'inline-flex', alignItems: 'center', gap: '5px', padding: '4px 10px', borderRadius: '20px', fontSize: '0.7rem', fontWeight: '700', color: C.green, background: C.greenDim, border: `1px solid ${C.greenBorder}` }}>
                <i className="pi pi-check-circle" style={{ fontSize: '0.65rem' }} /> {feesSummary.fullyPaidCount} Fully Paid
              </span>
              <span style={{ display: 'inline-flex', alignItems: 'center', gap: '5px', padding: '4px 10px', borderRadius: '20px', fontSize: '0.7rem', fontWeight: '700', color: '#a06000', background: C.goldDim, border: `1px solid ${C.goldBorder}` }}>
                <i className="pi pi-clock" style={{ fontSize: '0.65rem' }} /> {feesSummary.partialCount} Partial
              </span>
              <span style={{ display: 'inline-flex', alignItems: 'center', gap: '5px', padding: '4px 10px', borderRadius: '20px', fontSize: '0.7rem', fontWeight: '700', color: C.red, background: C.redDim, border: `1px solid ${C.redBorder}` }}>
                <i className="pi pi-times-circle" style={{ fontSize: '0.65rem' }} /> {feesSummary.notPaidCount} Unpaid
              </span>
            </div>

            <div style={{ display: 'grid', gridTemplateColumns: 'minmax(280px, 1.1fr) minmax(320px, 1.4fr)', gap: '24px', alignItems: 'start' }}>

              {/* Bar chart comparing enrollments */}
              <div style={{ padding: '14px 16px', borderRadius: '12px', border: `1px solid ${C.border}`, background: '#fbfbfd' }}>
                <p style={{ margin: '0 0 14px 0', fontSize: '0.78rem', fontWeight: '700', color: C.navy }}>Paid vs Balance by Enrollment</p>
                <FeesBarChart data={feesDetails} />
              </div>

              {/* Detailed enrollment list */}
              <div style={{ display: 'flex', flexDirection: 'column', gap: '10px' }}>
                {feesDetails.map(fd => {
                  const meta = FEE_STATUS_META[fd.fees.payment_status];
                  const paidPct = fd.fees.total_fee > 0
                    ? Math.min(100, Math.round((fd.fees.total_paid / fd.fees.total_fee) * 100))
                    : 0;
                  const duration = [
                    formatMonthYear(fd.start_month, fd.start_year),
                    formatMonthYear(fd.end_month, fd.end_year),
                  ].filter(Boolean).join(' – ');
                  const expanded = !!feesExpanded[fd.enrollment_id];

                  return (
                    <div key={fd.enrollment_id} style={{ borderRadius: '11px', border: `1px solid ${C.border}`, overflow: 'hidden' }}>
                      <div
                        className="db-feed-item"
                        onClick={() => toggleFeeExpanded(fd.enrollment_id)}
                        style={{ display: 'flex', gap: '12px', padding: '12px 14px' }}
                      >
                        <div style={{ width: '34px', height: '34px', flexShrink: 0, borderRadius: '10px', background: `${meta.color}1a`, border: `1px solid ${meta.color}40`, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                          <i className="pi pi-wallet" style={{ fontSize: '0.85rem', color: meta.color }} />
                        </div>
                        <div style={{ flex: 1, minWidth: 0 }}>
                          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: '8px', marginBottom: '3px' }}>
                            <p style={{ margin: 0, fontSize: '0.83rem', fontWeight: '700', color: C.body, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                              {fd.course_name ?? fd.course_title ?? 'Course'}
                            </p>
                            <span style={{
                              flexShrink: 0, fontSize: '0.62rem', fontWeight: '700', padding: '2px 8px', borderRadius: '20px',
                              color: meta.color, background: `${meta.color}1a`,
                            }}>
                              {meta.label}
                            </span>
                          </div>
                          <p style={{ margin: '0 0 6px 0', fontSize: '0.78rem', color: C.sub, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                            {fd.batch_name ?? 'All Batches'}{fd.batch_time ? ` · ${fd.batch_time}` : ''}
                          </p>

                          {/* Progress bar */}
                          <div style={{ width: '100%', height: '5px', borderRadius: '3px', background: '#eceef1', overflow: 'hidden', marginBottom: '6px' }}>
                            <div style={{ width: `${paidPct}%`, height: '100%', borderRadius: '3px', background: meta.color }} />
                          </div>

                          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: '8px' }}>
                            <span style={{ fontSize: '0.73rem', color: C.navy, fontWeight: '600' }}>
                              {formatCurrency(fd.fees.total_paid)} <span style={{ color: C.muted, fontWeight: '500' }}>/ {formatCurrency(fd.fees.total_fee)}</span>
                            </span>
                            {fd.fees.balance_due > 0 && (
                              <span style={{ fontSize: '0.71rem', color: '#b91c1c', fontWeight: '700' }}>
                                Due {formatCurrency(fd.fees.balance_due)}
                              </span>
                            )}
                          </div>
                        </div>
                        <button
                          type="button"
                          className="db-fee-expand-btn"
                          onClick={e => { e.stopPropagation(); toggleFeeExpanded(fd.enrollment_id); }}
                          style={{ flexShrink: 0, alignSelf: 'center', width: '24px', height: '24px', background: 'transparent', border: 'none', cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center', color: C.muted, borderRadius: '6px' }}
                        >
                          <i className={`pi ${expanded ? 'pi-chevron-up' : 'pi-chevron-down'}`} style={{ fontSize: '0.7rem' }} />
                        </button>
                      </div>

                      {expanded && (
                        <div className="db-fee-detail-row" style={{ padding: '12px 14px', background: '#fbfbfd', borderTop: `1px solid ${C.border}`, display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(130px, 1fr))', gap: '10px' }}>
                          <div>
                            <p style={{ margin: '0 0 2px 0', fontSize: '0.6rem', color: C.muted, fontWeight: '700', textTransform: 'uppercase' }}>Base Fee</p>
                            <p style={{ margin: 0, fontSize: '0.76rem', color: C.body, fontWeight: '700' }}>{formatCurrency(fd.fees.base_fee)}</p>
                          </div>
                          <div>
                            <p style={{ margin: '0 0 2px 0', fontSize: '0.6rem', color: C.muted, fontWeight: '700', textTransform: 'uppercase' }}>Additional Fee</p>
                            <p style={{ margin: 0, fontSize: '0.76rem', color: C.body, fontWeight: '700' }}>{formatCurrency(fd.fees.additional_fee)}</p>
                          </div>
                          <div>
                            <p style={{ margin: '0 0 2px 0', fontSize: '0.6rem', color: C.muted, fontWeight: '700', textTransform: 'uppercase' }}>Payments Made</p>
                            <p style={{ margin: 0, fontSize: '0.76rem', color: C.body, fontWeight: '700' }}>{fd.fees.payment_count}</p>
                          </div>
                          <div>
                            <p style={{ margin: '0 0 2px 0', fontSize: '0.6rem', color: C.muted, fontWeight: '700', textTransform: 'uppercase' }}>Last Payment</p>
                            <p style={{ margin: 0, fontSize: '0.76rem', color: C.body, fontWeight: '700' }}>{formatDate(fd.fees.last_payment_date)}</p>
                          </div>
                          <div>
                            <p style={{ margin: '0 0 2px 0', fontSize: '0.6rem', color: C.muted, fontWeight: '700', textTransform: 'uppercase' }}>Admitted</p>
                            <p style={{ margin: 0, fontSize: '0.76rem', color: C.body, fontWeight: '700' }}>{formatDate(fd.date_of_admission)}</p>
                          </div>
                          {duration && (
                            <div>
                              <p style={{ margin: '0 0 2px 0', fontSize: '0.6rem', color: C.muted, fontWeight: '700', textTransform: 'uppercase' }}>Course Duration</p>
                              <p style={{ margin: 0, fontSize: '0.76rem', color: C.body, fontWeight: '700' }}>{duration}</p>
                            </div>
                          )}
                        </div>
                      )}
                    </div>
                  );
                })}
              </div>
            </div>
          </div>
        )}
      </div>

    </div>
  );
};

export default Dashboard;